import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PageType } from "../constants";
import {
  CrmApplicationStatus,
  CrmPitchConceptStatus,
  CurrentPitchConcept,
} from "../models/crmStatus";
import {
  PitchInformationAboutYourGameDataType,
  PitchInformationAboutYourselfDataType,
  PitchInformationAskForXboxDataType,
  PitchInformationPublisherDeveloperDataType,
} from "../models/forms";
import { Programs } from "../models/programsConfig";
import { PitchApplicationStep, PitchApplicationStepsOrder } from "../models/status";
import {
  PitchInformationFormId,
  PitchInformationSubFormsId,
} from "../pages/PitchInformationPage/PitchInformationPageConstants";
import { statusActions } from "../redux/actions/statusActions";
import { OnboardingRootState } from "../redux/reducers/rootReducer";
import {
  getCrmApplicationStatus,
  getCurrentPitchConcept,
  getIsInitialCrmFetchDone,
  getOnboardingDecision,
  getPitchApplicationStep,
  getSubFormData,
} from "../redux/selectors";
import { getOnboardingSignedIn } from "../redux/selectors/authSelectors";
import { compareCrmApplicationStatus } from "./commonStatusHelpers";
import {
  isPitchAboutYourGameValid,
  isPitchAboutYourselfValid,
  isPitchAskForXboxValid,
  isPitchInformationPublisherDeveloperValid,
} from "./formValidation";

export const comparePitchApplicationStepStatus = (
  step1: PitchApplicationStep,
  step2: PitchApplicationStep
) => {
  const indexStep1 = PitchApplicationStepsOrder.indexOf(step1);
  const indexStep2 = PitchApplicationStepsOrder.indexOf(step2);
  return indexStep1 < indexStep2 ? -1 : indexStep1 > indexStep2 ? 1 : 0;
};

export const checkPitchConceptStep = async (
  step: PitchApplicationStep,
  crmPitchConceptStatus: CrmPitchConceptStatus | undefined,
  pitchInfoPublisherDeveloperSubFormData: PitchInformationPublisherDeveloperDataType,
  pitchInfoAboutYourGameSubFormData: PitchInformationAboutYourGameDataType,
  pitchInfoAskForXboxSubFormData: PitchInformationAskForXboxDataType,
  pitchInfoAboutYourselfSubFormData: PitchInformationAboutYourselfDataType
): Promise<PitchApplicationStep> => {
  if (
    step !== PitchApplicationStep.PitchInformation ||
    !pitchInfoPublisherDeveloperSubFormData ||
    !pitchInfoAboutYourGameSubFormData ||
    !pitchInfoAskForXboxSubFormData ||
    !pitchInfoAboutYourselfSubFormData
  ) {
    return step;
  }

  switch (crmPitchConceptStatus) {
    case CrmPitchConceptStatus.Submitted:
      return PitchApplicationStep.ApplicationSubmitted;
    case CrmPitchConceptStatus.Reviewed:
      return PitchApplicationStep.ApplicationReviewed;
  }

  // ELSE see if the application is ready for review (data meets all schema conditions)
  const isPitchInfoPublisherDeveloperFormValid = await isPitchInformationPublisherDeveloperValid(
    pitchInfoPublisherDeveloperSubFormData
  );
  const isPitchInfoAboutYourGameFormValid = await isPitchAboutYourGameValid(
    pitchInfoAboutYourGameSubFormData
  );

  const isPitchInfoAskForXboxFormValid = await isPitchAskForXboxValid(
    pitchInfoAskForXboxSubFormData
  );

  const isPitchInfoAboutYourselfFormValid = await isPitchAboutYourselfValid(
    pitchInfoAboutYourselfSubFormData
  );

  return isPitchInfoPublisherDeveloperFormValid &&
    isPitchInfoAboutYourGameFormValid &&
    isPitchInfoAskForXboxFormValid &&
    isPitchInfoAboutYourselfFormValid
    ? PitchApplicationStep.ApplicationReadyForReview
    : step;
};

export const calculatePitchApplicationStep = async (
  currentCrmPitchApplicationStatus: CrmApplicationStatus | undefined,
  crmPitchConceptStatus: CrmPitchConceptStatus | undefined,
  pitchInfoPublisherDeveloperSubFormData: PitchInformationPublisherDeveloperDataType,
  pitchInfoAboutYourGameSubFormData: PitchInformationAboutYourGameDataType,
  pitchInfoAskForXboxSubFormData: PitchInformationAskForXboxDataType,
  pitchInfoAboutYourselfSubFormData: PitchInformationAboutYourselfDataType
) => {
  /** ********************************** Steps logic ************************************/

  // NDA under review
  const isNdaUnderReview = currentCrmPitchApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmPitchApplicationStatus,
        CrmApplicationStatus.NdaUnderReview
      ) === 0
    : false;
  if (isNdaUnderReview) {
    return PitchApplicationStep.NdaUnderReview;
  }

  // NDA sent
  const isNdaSent = currentCrmPitchApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmPitchApplicationStatus,
        CrmApplicationStatus.NdaSent
      ) === 0
    : false;
  if (isNdaSent) {
    return PitchApplicationStep.NdaSent;
  }

  // NDA signed
  const isNdaSigned = currentCrmPitchApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmPitchApplicationStatus,
        CrmApplicationStatus.NdaSigned
      ) === 0
    : false;

  if (isNdaSigned) {
    return checkPitchConceptStep(
      PitchApplicationStep.PitchInformation,
      crmPitchConceptStatus,
      pitchInfoPublisherDeveloperSubFormData,
      pitchInfoAboutYourGameSubFormData,
      pitchInfoAskForXboxSubFormData,
      pitchInfoAboutYourselfSubFormData
    );
  }

  return PitchApplicationStep.Unset;
};

export const useCalculatePitchStatus = async (pageType: PageType | undefined) => {
  const dispatch = useDispatch();

  const isSignedIn: boolean = useSelector(getOnboardingSignedIn);
  const currentStep: PitchApplicationStep | undefined = useSelector(getPitchApplicationStep);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);

  // Application submission
  const currentCrmPitchApplicationStatus = useSelector(getCrmApplicationStatus);
  const onboardingDecision = useSelector(getOnboardingDecision);

  // Pitch Information sub form data
  const pitchInfoPublisherDeveloperSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(
      state,
      PitchInformationFormId,
      PitchInformationSubFormsId.PublisherDeveloperInformation
    )
  ) as PitchInformationPublisherDeveloperDataType;

  const pitchInfoAboutYourGameSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, PitchInformationFormId, PitchInformationSubFormsId.AboutYourGame)
  ) as PitchInformationAboutYourGameDataType;

  const pitchInfoAskForXboxSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, PitchInformationFormId, PitchInformationSubFormsId.AskForXbox)
  ) as PitchInformationAskForXboxDataType;

  const pitchInfoAboutYourselfSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, PitchInformationFormId, PitchInformationSubFormsId.AboutYourself)
  ) as PitchInformationAboutYourselfDataType;

  const currentPitchConcept: CurrentPitchConcept | undefined = useSelector(getCurrentPitchConcept);

  useEffect(() => {
    const calculateNextStep = async () => {
      // 0: Must be signed in
      if (!isSignedIn || pageType !== PageType.PitchApplication || !isInitialCrmFetchDone) {
        return;
      }

      const step = await calculatePitchApplicationStep(
        currentCrmPitchApplicationStatus,
        currentPitchConcept?.crmPitchConceptStatus,
        pitchInfoPublisherDeveloperSubFormData,
        pitchInfoAboutYourGameSubFormData,
        pitchInfoAskForXboxSubFormData,
        pitchInfoAboutYourselfSubFormData
      );

      if (step !== currentStep) {
        dispatch(statusActions.updateStep(step, Programs.Pitch));
      }
    };

    calculateNextStep();
  }, [
    pageType,
    currentCrmPitchApplicationStatus,
    currentStep,
    dispatch,
    isSignedIn,
    onboardingDecision,
    isInitialCrmFetchDone,
    currentPitchConcept,
    pitchInfoPublisherDeveloperSubFormData,
    pitchInfoAboutYourGameSubFormData,
    pitchInfoAskForXboxSubFormData,
    pitchInfoAboutYourselfSubFormData,
  ]);
};
